/** @jsxImportSource @emotion/react */
import React from "react";
import Immutable from "immutable";
import moment from "moment";
import * as KpiRepo from "js/common/repo/backbone/kpi-repo";
import * as KpiCalculator from "js/common/kpi-calculator";
import * as DashboardUtils from "js/dashboards/utils";
import {renderUserOrGroupDetails} from "js/dashboards/utils";
import * as TimeframeRepo from "js/common/repo/backbone/timeframe-repo";
import * as Users from "js/common/users";
import * as Groups from "js/common/groups";
import * as Numbers from "js/common/utils/numbers";
import DataConfigToolbar from "js/dashboards/data-config-toolbar";
import KpiPicker from "js/common/views/kpis/kpi-picker";
import Checkbox from "js/common/views/inputs/checkbox";
import * as Overlays from "js/dashboards/components/overlays";
import * as Formatter from "js/common/utils/formatter";
import {RadioGroup as RadioButtonGroup} from "@mui/material";
import RadioButton from "js/common/views/inputs/radio-button";
import LeaderboardRow from "js/dashboards/components/leaderboard/leaderboard-row";
import {betterMemo} from "js/common/utils/more-memo";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import KpiDetailsDialog from "js/oneview/kpi-details/dialog";


const getPercentageOfTargetComplete = (value, target) => {
  if (!target || target === 0) {
    return 0;
  }
  return value / target;
};

const rankByValue = (data) => {
  return data.sortBy(
      item => -item.getIn(["kpi", "total", "value"])
  ).map((item, index) => item.set("rank", index + 1));
};

const rankByPercent = (data) => {
  return data.sortBy(item => {
    const target = item.getIn(["kpi", "target", "value"]);
    const value = item.getIn(["kpi", "total", "value"]);
    if (!target || target === 0) {
      return Number.MAX_SAFE_INTEGER; // Ensures these sort last
    }
    return -(value / target);
  }).map((item, index) => item.set("rank", index + 1));
};

const mapToTableRow = (data, kpiId) => {
  const rank = data.get("rank");
  const userId = data.get("userId");
  const kpi = data.get("kpi");
  const kpiConfig = KpiRepo.get(kpiId);
  const valueFormat = kpiConfig.get("valueFormat");
  const target = kpi.get("target");
  const total = kpi.get("total");
  const totalValueDisplay = Formatter.format(total.toJS(), {valueFormat, maxDisplayLength: 3});
  const hasTarget = target && target.get("value") > 0;
  const targetValueDisplay = hasTarget ? Formatter.format(target.toJS(), {valueFormat, maxDisplayLength: 3}) : "";
  const percentOfTarget = hasTarget ? getPercentageOfTargetComplete(total.get("value"), target.get("value")) : "";
  return Immutable.fromJS({
    rank,
    userId,
    totalValueDisplay,
    targetValueDisplay,
    percentOfTarget
  });
};

const rankData = (data, sortField, sortMode, filterZero, kpiId) => {
  const sortFn = sortField === "VALUE" ? rankByValue : rankByPercent;
  const sortedData = sortFn(data)
      .filter(row => filterZero ? row.getIn(["kpi", "total", "value"]) !== 0 && (sortField
          === "VALUE"
          || row.getIn(["kpi", "target", "value"])) !== 0 : true)
      .map(row => mapToTableRow(row, kpiId));

  if (sortMode === "DESC") {
    return sortedData;
  }

  return sortedData.reverse();
};

const Leaderboard = betterMemo({displayName: "Leaderboard"}, ({
  dimensions,
  data,
  config,
  onRequestDialog,
  onParentConfigChange,
  panelColor = null
}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const kpiId = config.get("kpiId");
  const filterZero = config.get("filterZero", false);

  if (!kpiId) {
    return <Overlays.Message coverOtherContent={false}>
      Choose a metric to display the leaderboard
    </Overlays.Message>;
  }

  if (!data || !data.get("value") || data.get("value").size === 0) {
    return <Overlays.Message coverOtherContent={false}>
      No data available for this metric
    </Overlays.Message>;
  }

  const handleRowClick = (userId) => renderDialog(config, onParentConfigChange, onRequestDialog, userId);

  const rankedData = rankData(
      data.get("value"),
      config.get("sortField"),
      config.get("sortMode"),
      filterZero,
      kpiId);

  const renderContent = () => {
    return (
        <div
            data-test-id="leaderboard"
            style={{
              height: "100%",
              flex: 1,
              padding: "0 15px",
              marginBottom: 10
            }}>
          {rankedData.isEmpty() && <div
              style={{
                display: "flex",
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
                fontSize: 13
              }}>No data to display</div>}
          {rankedData.map((rowData, index) => {
            const userId = rowData.get("userId");
            const user = Users.getUser(userId);
            const totalValue = rowData.get("totalValueDisplay");
            const targetValue = rowData.get("targetValueDisplay");
            const percentOfTarget = rowData.get("percentOfTarget");
            const percentOfTargetStr = percentOfTarget ? Numbers.toPercentageStr(percentOfTarget) : "--";
            const rank = index + 1;

            return (
                <LeaderboardRow
                    key={userId}
                    rank={rank}
                    user={user}
                    value={totalValue}
                    target={targetValue}
                    percentOfTarget={percentOfTargetStr}
                    onClick={handleRowClick}
                    sortField={config.get("sortField")}
                />
            );
          })}
        </div>
    );
  };

  return (
      <div
          style={{
            height: dimensions.height - 50,
            display: "flex",
            flexDirection: "column",
            backgroundColor: panelColor || theme.palette.background.card,
            padding: "10px 0",
            overflow: "auto"
          }}>
        {renderContent()}
      </div>
  );
});

// Editor component
const LeaderboardEditor = React.memo(({
  component,
  onComponentChange
}) => {
  const config = component.get("dataConfig");
  const {theme} = React.useContext(CustomThemeContext);

  const onConfigChange = React.useCallback(newDataConfig => {
    onComponentChange(component.set("dataConfig", newDataConfig));
  }, [onComponentChange, component]);

  const kpis = React.useMemo(
      () => Immutable.fromJS(KpiRepo
          .getAll().toJSON())
          .filter(k => k.get("visible") || k.get("id") === config.get("kpiId")),
      [config]
  );

  const optionHeadingStyle = {
    fontSize: 14,
    fontWeight: "bold",
    margin: 0,
    marginTop: 30,
    padding: 0
  };

  const optionDescriptionStyle = {
    fontSize: 13,
    margin: 0,
    padding: 0,
    opacity: 0.7,
    lineHeight: 1.3
  };

  const formControlWrapperStyle = {
    background: theme.palette.background.card,
    border: `1px solid ${theme.palette.border.lightest}`,
    padding: "2px 5px",
    marginTop: 5,
    borderRadius: 5
  };

  return (
      <div style={{padding: "0.5rem"}}>
        <DataConfigToolbar config={config} onConfigChange={onConfigChange} />

        <div style={{width: "50%", marginBottom: "0.3rem", marginTop: "2rem", marginLeft: "1rem"}}>
          <p style={{...optionHeadingStyle, marginTop: 20}}>What metric would you like to use for your leaderboard?</p>
          <KpiPicker
              placeholder="Choose a metric for your leaderboard"
              multiSelect={false}
              clearable={false}
              kpis={kpis}
              selectedKpiId={config.get("kpiId")}
              onChange={kpiId => {
                const kpi = KpiRepo.get(kpiId);
                onConfigChange(
                    config
                        .set("kpiId", kpiId)
                        .set("title", kpi.get("name"))
                );
              }}
          />


          <p style={optionHeadingStyle}>Filter out zero values?</p>
          <p style={optionDescriptionStyle}>If you select this option we wont include users with 0 as their value. This
            can be useful if you have a large amount of users and want to avoid clutter.</p>
          <div style={formControlWrapperStyle}>
            <Checkbox
                style={{marginTop: 0}}
                label="Filter out users who have a value of 0"
                checked={config.get("filterZero", false)}
                onCheck={(e, isChecked) => {
                  onConfigChange(config.set("filterZero", isChecked));
                }}
            />
          </div>


          <p style={optionHeadingStyle}>What would you like to rank by?</p>
          <p style={optionDescriptionStyle}>For most cases ranking by value is recommended. However there are some
            cases, like if you are comparing senior recruiters to junior recruiters where you may to choose to rank by %
            of their target achieved.</p>
          <RadioButtonGroup
              style={formControlWrapperStyle}
              name="sortField"
              value={config.get("sortField")}
              onChange={e => {
                onConfigChange(config.set("sortField", e.target.value));
              }}>
            <div style={{display: "flex"}}>
              <RadioButton
                  value="VALUE"
                  label="Value" />
              <RadioButton
                  value="PERCENTAGE"
                  label="Percentage of users' target" />
            </div>
          </RadioButtonGroup>


          <p style={optionHeadingStyle}>What order would you like to show the leaderboard?</p>
          <p style={optionDescriptionStyle}>Descending is usually the most intuitive. Position 1,2,3 and so on.
            Depending on your metric though, sometimes being 1st isn't always a good thing!</p>
          <RadioButtonGroup
              style={formControlWrapperStyle}
              name="sortMode"
              value={config.get("sortMode")}
              onChange={e => {
                onConfigChange(config.set("sortMode", e.target.value));
              }}>
            <div style={{display: "flex"}}>
              <RadioButton
                  value="DESC"
                  label="Descending" />
              <RadioButton
                  value="ASC"
                  label="Ascending" />
            </div>
          </RadioButtonGroup>
        </div>
      </div>
  );
});

const renderDialog = (config, onParentConfigChange, onRequestDialog, userId) => {
  const kpi = KpiRepo.get(config.get("kpiId"));

  const kpiDetailsOptions = {
    kpiId: config.get("kpiId"),
    timeframe: TimeframeRepo.parse(config.get("timeframe").toJS()),
    clientIds: config.getIn(["clientFilter", "allClientIds"], Immutable.List()),
    entityToIds: config.get("entityToIds", Immutable.Map()).toObject(),
    ...DashboardUtils.configToQualifierAjaxParams(config)
  };

  const dialogConfig = config.set("qualifier", Immutable.Map({
    type: "USER",
    id: userId
  }));

  const title = (
      <div style={{display: "inline-block"}}>
      <span
          style={{
            fontSize: 20,
            textTransform: "none",
            fontWeight: 500
          }}>
          {kpi.get("name")}
      </span>
        <span style={{fontWeight: 500}}>
        {` (${TimeframeRepo.parse(config.get("timeframe").toJS()).attributes.name})`}
      </span>
        {renderUserOrGroupDetails(dialogConfig)}
      </div>
  );
  const kpiDialogRef = React.createRef();
  const handleKpiDialogRequestClose = () => closeDialog();
  const handleDialogRequestClose = () => kpiDialogRef.current.handleCloseRequest();
  const closeDialog = onRequestDialog(
      <KpiDetailsDialog
          ref={kpiDialogRef}
          userId={userId}
          renderContentOnly
          {...kpiDetailsOptions}
          matchAnyTagIds={config.getIn(["tagFilter", "matchAnyTagIds"], Immutable.List())}
          matchAllTagIds={config.getIn(["tagFilter", "matchAllTagIds"], Immutable.List())}
          excludedTagIds={config.getIn(["tagFilter", "excludedTagIds"], Immutable.List())}
          onRequestClose={handleKpiDialogRequestClose}
          onGroupClick={groupId => {
            onParentConfigChange(parentConfig => parentConfig.set("qualifier", Immutable.Map({
              type: "GROUP",
              id: groupId
            })), true);
            closeDialog();
          }}
          onUserClick={userId => {
            onParentConfigChange(parentConfig => parentConfig.set("qualifier", Immutable.Map({
              type: "USER",
              id: userId
            })), true);
            closeDialog();
          }} />,
      {title: title, onRequestClose: handleDialogRequestClose});
};

export default Immutable.fromJS({
  type: "Leaderboard",
  label: "Leaderboard",
  titleIsEditable: true,
  getTitle: component => {
    const config = component.get("dataConfig");
    const kpiId = config.get("kpiId");

    if (kpiId) {
      if (component.get("title") || component.get("title") === "") {
        return component.get("title");
      } else {
        return `${KpiRepo.get(kpiId).get("name")} Leaderboard`;
      }
    } else {
      return "New Leaderboard";
    }
  },
  getDefaultData: () => Immutable.Map(),
  getReactComponent: () => Leaderboard,
  getEditorReactComponent: () => LeaderboardEditor,
  canFullScreen: true,
  layout: {
    min: {width: 16, height: 8},
    max: {width: 48, height: 48},
    init: {width: 16, height: 16}
  },
  applyParentConfig: (parentConfig, dataConfig) => DashboardUtils.applyInheritanceForKeys(parentConfig, dataConfig),
  load: component => {
    const config = component.get("dataConfig");
    const kpiId = config.get("kpiId");

    if (!kpiId) {
      return Promise.resolve(Immutable.List());
    }

    const ajaxParams = {
      dateFromUI: moment().format("YYYY-MM-DD"),
      timeframe: TimeframeRepo.parse(config.get("timeframe").toJS()),
      clientIds: config.getIn(["clientFilter", "allClientIds"]).toArray(),
      matchAnyTagIds: config.getIn(["tagFilter", "matchAnyTagIds"]).toArray(),
      matchAllTagIds: config.getIn(["tagFilter", "matchAllTagIds"]).toArray(),
      excludedTagIds: config.getIn(["tagFilter", "excludedTagIds"]).toArray(),
      entityToIds: config.get("entityToIds", Immutable.Map()).toObject(),
      ...DashboardUtils.configToQualifierAjaxParams(config),
      alwaysAllowClientFilter: true
    };

    const qualifierType = config.getIn(["qualifier", "type"], "GROUP");
    const qualifierId = config.getIn(["qualifier", "id"], Groups.getRootGroup().get("id"));

    return KpiCalculator
        .targetedLeaderboard(kpiId, {
          ...ajaxParams,
          qualifierType,
          qualifierId
        })
        .then(data => Immutable.fromJS(data));
  }
});